import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { DotPattern } from './types';
import { useKTTS } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { K_PRACTICE_TIMER_CONSTANTS } from '../shared';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';

interface YouDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

const YouDoScreen: React.FC<YouDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<{ factId: string; digit: number }[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);

  // Subitizing-specific states
  const [flashPhase, setFlashPhase] = useState<'ready' | 'flashing' | 'blank' | 'question'>('ready');
  const [showDots, setShowDots] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<DotPattern | null>(null);

  const { playTTSWithCallback, playChime, isTTSPlaying } = useKTTS();

  // Generate dot pattern using 3x3 grid
  const generateDotPattern = useCallback((quantity: number): DotPattern => {
    // 3x3 grid positions - edges and center of the large card area
    const gridPositions = [
      {x: 10, y: 10}, {x: 50, y: 10}, {x: 90, y: 10},  // Top row
      {x: 10, y: 50}, {x: 50, y: 50}, {x: 90, y: 50},  // Middle row
      {x: 10, y: 90}, {x: 50, y: 90}, {x: 90, y: 90}   // Bottom row
    ];

    // Shuffle positions and select the first 'quantity' positions
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Create dots from selected positions
    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots
    };
  }, []);

  // Generate answer choices (target quantity + 3 other options)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Add 3 other numbers between 1-6, avoiding target
    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6) + 1; // 1-6
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();
  const currentDigit = currentFact?.digit;  // digit field contains quantity

  // Generate new pattern when digit changes
  useEffect(() => {
    if (currentDigit !== undefined) {
      setCurrentPattern(generateDotPattern(currentDigit));
      setAnswerChoices(generateAnswerChoices(currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setFlashPhase('ready');
    }
  }, [currentDigit, generateDotPattern, generateAnswerChoices]);

  // Start the subitizing sequence when pattern changes
  useEffect(() => {
    if (currentPattern && flashPhase === 'ready') {
      // Small delay then start flash
      setTimeout(() => {
        setFlashPhase('flashing');
        setShowDots(true);

        // Flash for K duration (800ms)
        setTimeout(() => {
          setShowDots(false);
          setFlashPhase('blank');

          // Brief blank period, then show question
          setTimeout(() => {
            setFlashPhase('question');
            // TTS will play from useEffect below if enabled
          }, 100);
        }, K_PRACTICE_TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
      }, 500);
    }
  }, [currentPattern, flashPhase]);

  // Play TTS instruction when entering question phase (optional for You Do)
  useEffect(() => {
    if (flashPhase === 'question' && !ttsHasPlayed && settings.speechEnabled && currentDigit !== undefined) {
      // Use existing subitizing instruction audio
      const audioFile = {
        filename: 'subitizing_instruction.mp3',
        text: 'How many dots did you see?'
      };

      // Play with shorter delay since this is independent practice
      setTimeout(() => {
        playTTSWithCallback(audioFile, () => {
          setTtsHasPlayed(true);
        }).catch(() => {
          setTtsHasPlayed(true);
        });
      }, 200);
    } else if (flashPhase === 'question' && !settings.speechEnabled) {
      setTtsHasPlayed(true);
    }
  }, [flashPhase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, currentDigit]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      // In retry phase - after removing an item, check if queue is empty
      if (failedQueue.length === 0) {
        // Queue is empty, we're done!
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        // Current index is beyond queue length, reset to 0
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        // Move to next item in retry queue
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        // At end of retry queue, restart from beginning
        setCurrentRetryIndex(0);
      }
    } else {
      // In original phase
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // Finished original sequence
        if (failedQueue.length > 0) {
          // Start retry phase
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          // All done!
          onComplete();
        }
      }
    }

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
    setFlashPhase('ready');
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback((selectedDigit: number) => {
    // Don't allow clicks while TTS is playing or already answered
    if (isTTSPlaying || hasAnswered) return;

    setHasAnswered(true);
    const isCorrect = selectedDigit === currentDigit;

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Update fact score: +1 for correct
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        // Only increment counter if going from 0 to 1 (first genuine correct)
        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);

          // Update backend fact status to accuracyPractice when score reaches 1
          if (user?.userId) {
            setTimeout(async () => {
              try {
                const progressUpdate: SetUserProgressRequest = {
                  facts: {
                    [currentFact.factId]: {
                      status: 'accuracyPractice'
                    }
                  }
                };

                await setUserProgress(user.userId, progressUpdate);
              } catch (error) {
                console.error('Failed to update fact status to accuracyPractice:', error);
              }
            }, 0);
          }
        }

        return newScores;
      });

      if (isRetryPhase) {
        // In retry phase - remove from failed queue
        setFailedQueue(prev => {
          const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
          // Check if we need to complete after this removal
          if (newQueue.length === 0) {
            setTimeout(() => onComplete(), 600);
          } else {
            // Adjust currentRetryIndex after removal using functional update to prevent race condition
            setTimeout(() => {
              setCurrentRetryIndex(prevIndex =>
                prevIndex >= newQueue.length ? 0 : prevIndex
              );
            }, 600);
          }
          return newQueue;
        });
      }

      // Play chime and advance after a delay (but not in retry phase where we handle advancement in setFailedQueue)
      playChime().then(() => {
        if (!isRetryPhase) {
          setTimeout(() => {
            advanceToNext();
          }, 500);
        }
      });
    } else {
      // Incorrect answer

      // Update fact score: -1 for incorrect
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        // If score goes below 1, decrement counter (was previously counted but now incorrect)
        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      if (!correctAnswerGiven) {
        // First attempt was wrong - add to failed queue (only if not already there)
        if (!isRetryPhase) {
          setFailedQueue(prev => {
            const alreadyExists = prev.some(f => f.factId === currentFact.factId);
            if (!alreadyExists) {
              return [...prev, currentFact];
            }
            return prev;
          });
        } else {
          // In retry phase and still wrong - add back to end of failed queue
          const failedFact = failedQueue[currentRetryIndex];
          setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
        }
      }

      // Reset to allow another attempt after feedback
      setTimeout(() => {
        setHasAnswered(false);
      }, 1500);
    }
  }, [currentDigit, hasAnswered, isTTSPlaying, playChime, correctAnswerGiven, isRetryPhase, currentFact, failedQueue, currentRetryIndex, advanceToNext, onComplete, user]);

  if (!currentPattern || !currentFact) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row - Back Button, Practice Indicator and Progress */}
      <div className="flex justify-between items-center p-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
        >
          ← Back
        </button>

        {/* Practice Indicator */}
        <div className="bg-orange-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg">
          🌟 You Do Practice
        </div>

        {/* Progress Display */}
        <div className="relative w-16 h-16 opacity-40">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="1.5"
            />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="87.96"
              strokeDashoffset={87.96 * (1 - (completedCount / factsToPractice.length))}
              className="transition-all duration-300 ease-out"
            />
          </svg>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">

          {/* Ready Phase: Waiting */}
          {flashPhase === 'ready' && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">Get ready...</div>
              <div className="text-lg text-gray-500">Watch carefully!</div>
            </div>
          )}

          {/* Dots Container - Uses the exact same area as the 4 option cards */}
          {flashPhase === 'flashing' && showDots && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12 relative">
              {/* Create the exact same 4-card layout area */}
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>

              {/* Dots positioned absolutely over this area - 3x3 grid positions */}
              {currentPattern.dots.map((dot) => (
                <motion.div
                  key={dot.id}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full shadow-lg"
                  style={{
                    left: `${dot.x}%`,
                    top: `${dot.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              ))}
            </div>
          )}

          {/* Blank Phase: Same exact area as cards */}
          {flashPhase === 'blank' && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
            </div>
          )}

          {/* Answer Choices - 2x2 grid */}
          {flashPhase === 'question' && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              {answerChoices.map((choice, index) => (
                <motion.button
                  key={choice}
                  initial={{ opacity: 0, y: 30, rotate: -5 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.08, rotate: 1 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => handleAnswerClick(choice)}
                  disabled={hasAnswered || isTTSPlaying}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                    ${hasAnswered && choice === currentDigit
                      ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                      : hasAnswered || isTTSPlaying
                      ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                    }
                    ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {choice}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default YouDoScreen;